<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { getContext, onMount, tick } from 'svelte';
    import {format} from 'date-fns'
    import route from '../Util/routes';
    import type { MyFitnessStores } from './stores';
    import { type TrainingPlanSettings, defaultTrainingPlanSettings} from '../Common/constants';
    import FitnessSignature from './FitnessSignature.svelte';
    import FitnessSignatureTab from './FitnessSignatureTab.svelte';
    import TrainingLoad from './TrainingLoad.svelte';
    import XpmcSettings from './XPMCSettings.svelte';
    import {getRequest} from '../Util/utils';
    import GeneralSettings from './GeneralSettings.svelte';
    import { Button } from '@/lib/components/ui/button';
        
    export let profileParams;
    export let manualTrainingLoads;
    export let manualFitnessSignatures;
    export let startingTL;

    const {trainingAdvice, trainingAdviceUpdatedAt, trainingPlan, userParams, cal_settings} = getContext<MyFitnessStores>('myFitness')
    
    //@ts-ignore
    let Xert = window.Xert;
    
    let loading = false;
    
    let settings: TrainingPlanSettings = $trainingPlan?.settings ?? {...defaultTrainingPlanSettings, ...$trainingPlan.settings};

    onMount(async () => {
        initRecalcProgressionModal();
    });

    function setTrainingAdvice(training_advice) {
        if (training_advice.timestamp)
            trainingAdviceUpdatedAt.set(new Date(training_advice.timestamp))
        trainingAdvice.set(training_advice);
    }
    
    function setTrainingPlan(t_plan) {
        console.log("setTrainingPlan!", t_plan);
        trainingPlan.set(t_plan);
    }

    const postTrainingProgramURL = route('UserController@postTrainingProgram');
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // What is saved, to send only what has been edited since it was shown (or last saved). The plan's settings go whole, as one
    // value; the week's availability a day at a time, as it is edited a day at a time.
    let savedSettings = '';
    let savedDays: Record<string, string> = {};
    const snapshotDays = () => Object.fromEntries(weekdays.map(d => [d, JSON.stringify($cal_settings?.defaults?.[d])]));
    // Taken once the sections have set themselves up, which may fill in settings that were missing
    // Whether anything on the General tab has been edited, which the Save All button waits for
    let sectionsChanged = false;
    $: settingsChanged = savedSettings !== '' && JSON.stringify(settings) !== savedSettings;
    $: daysChanged = Object.keys(savedDays).length > 0 && weekdays.some(d => JSON.stringify($cal_settings?.defaults?.[d]) !== savedDays[d]);
    $: generalChanged = settingsChanged || daysChanged || sectionsChanged;
    // The other tabs report their own
    let signatureChanged = false;
    let xpmcChanged = false;
    $: tabChanged = selected_tab === Tab.General ? generalChanged : selected_tab === Tab.Signature ? signatureChanged : xpmcChanged;

    onMount(async () => {
        await tick();
        savedSettings = JSON.stringify(settings);
        savedDays = snapshotDays();
    });

    const postJson = (url: string, body: any) => window.fetch(url, { ...getRequest(), method: 'POST', body: JSON.stringify(body) });

    // The weekdays that changed, in one request, as they were when Save All was pressed (days: the snapshot of each day)
    async function saveDays(changed: number[], days: Record<string, string>) {
        const defaults = Object.fromEntries(changed.map(dow => {
            const day = JSON.parse(days[weekdays[dow]]);
            const time = day.time ?? 'advisor';
            return [weekdays[dow], {
                availability: !!day.availability,
                time: time.length === 5 ? `${time}:00` : time,
                duration: day.duration ?? -1,
                fixed_availability: !!day.fixed_availability,
                intensity: day.availability ? (day.intensity ?? 'advisor') : 'advisor',
            }];
        }));
        const response = await postJson('/calendar/default-availabilities', { defaults });
        if (!response.ok) throw new Error(`The weekly availability was not saved (HTTP ${response.status})`);
        const data = await response.json();
        if (data?.result === false) throw new Error(data.msg ?? 'The weekly availability was not saved');
        // With today's weekday among them, the new advice
        if (data?.advice) setTrainingAdvice(data.advice);
    }

    async function updateSettings() {
        // What is sent becomes what is saved: an edit made while it is on its way stays to be saved
        const sentSettings = JSON.stringify(settings);
        const settingsChanged = sentSettings !== savedSettings;
        const days = snapshotDays();
        const changedDays = weekdays.map((d, i) => i).filter(i => days[weekdays[i]] !== savedDays[weekdays[i]]);
        // Nothing here to save (an edit of a section of the tab is saved by the section, with its own message)
        if (!settingsChanged && changedDays.length == 0) return;
        loading = true;
        try {
            if (changedDays.length > 0) {
                await saveDays(changedDays, days);
                savedDays = { ...savedDays, ...Object.fromEntries(changedDays.map(i => [weekdays[i], days[weekdays[i]]])) };
            }
            if (settingsChanged) {
                const data = await postJson(postTrainingProgramURL, { settings: JSON.parse(sentSettings) }).then(response => response.json());
                if (data.result == false) {
                    Xert.warning("Failed to update program settings.");
                    return;
                }
                savedSettings = sentSettings;
                if (data.training_plan) {
                    setTrainingPlan(data.training_plan);
                }
                if (data.training_advice) {
                    setTrainingAdvice(data.training_advice);
                }
            }
            Xert.global("Settings updated");
        } catch (e) {
            console.error(e);
            Xert.warning("Failed to update settings.");
        } finally {
            loading = false;
        }
    }
    
    enum Tab {
        General,
        Signature,
        IRParams,
    }

    let tabOptions = [
        {id:"signature", value:Tab.Signature, title:"Fitness Signatures"},
        {id:"ir-params", value:Tab.IRParams, title:"XPMC"},
        {id:"program", value:Tab.General, title:"General"},       
    ]

    let selected_tab: Tab = Tab.Signature;
    
    const setSelectedTab = (t: Tab) => {
        selected_tab = t;
    }

    let recalculateFitnessSignature;
    let fitnessSignatureTab;
    let recalculateTrainingLoad;
    let xpmcSettings;
    let generalSettings;


    function submitHandler(e:any){
        switch (selected_tab){
            case Tab.General:
                generalSettings?.submitHandler()
                updateSettings();
            break;
            case Tab.IRParams: 
                xpmcSettings?.submitHandler();
            break;
            case Tab.Signature: 
                fitnessSignatureTab?.submitHandler();
            break;
        }
    }
    
  function RecalculateProgression(event:any) {
    return window.fetch(route('UserController@getRecalculateProgression'));
  }

  function initRecalcProgressionModal(e?) {
    recalculateTrainingLoad?.setTrainingLoad(startingTL);
    recalculateTrainingLoad?.setActivity(startingTL);
    recalculateFitnessSignature?.setActivity(startingTL);
  } 
</script>
    
<div id="program-page-container">
    <div id="program-settings-modal" > 
        <div id="control-header">
            <ul class="nav nav-tabs nav-tabs-style override-tab-styles" id="program-tabs">
                {#each tabOptions as option}
                <li>
                    <a href={'#'+option.id} data-toggle="tab" on:click={() => setSelectedTab(option.value)}>
                        {option.title}
                    </a>
                </li>
                {/each}
            </ul>
            <div class="form-group" style="text-align: right;">
                <button type="button" data-toggle="modal" data-target="#recalcModal" class="btn btn-warning" on:click={initRecalcProgressionModal} style={(Tab.Signature == selected_tab)?"":"display:none;"}>Reset/Recalculate Entire Progression <i class="fa fa-warning"> </i></button>
                <Button id="profile-submit-btn" on:click={submitHandler} disabled={!tabChanged} title={tabChanged ? "" : "Nothing has changed"}>Save All</Button>
            </div>
        </div>
        <div id="signature" class={"program-type-container tab-pane active"} style={selected_tab === Tab.Signature ? "display: flex" : "display: none"}>
            <FitnessSignatureTab bind:this={fitnessSignatureTab} bind:changed={signatureChanged} profileParams={$trainingAdvice} ir_params={profileParams?.ir_params} data={$trainingAdvice.current_minute_powers} {manualFitnessSignatures} />
        </div>
        <div id="ir-params" class={"program-type-container tab-pane active"} style={selected_tab === Tab.IRParams ? "display: flex" : "display: none"}>
            <XpmcSettings bind:this={xpmcSettings} bind:changed={xpmcChanged} {profileParams} {manualTrainingLoads} />
        </div>
        <div id="hr-params" class={"program-type-container tab-pane active flex-col md:flex-row"} style={selected_tab === Tab.General ? "display: flex" : "display: none"}>
            <GeneralSettings bind:this={generalSettings} bind:changed={sectionsChanged} {profileParams} bind:settings calculatedDifficulty={$trainingAdvice?.difficulty} />
        </div>

        <!-- <div id="program" class={"program-type-container tab-pane active"} style={selected_tab === Tab.Program ? "display: flex" : "display: none"}>
            <div class="settings-container portlet light bordered full-height-content full-height-content-scrollable p-2 border-2 curve-portlet">
                <div class="settings-body"> -->
                    <!-- <div class="settings-field">
                        <div class="dropdown-title align-self-baseline">
                            Event Readiness
                            <a href="https://baronbiosys.com/glossary/event-readiness/" target="_help">
                                <span class="tooltip-icon"><span class="fa-light fa-circle-question"></span></span>
                            </a>
                        </div>
                        <div class="slider-container">
                            <input type="range" min="1" max="5" step="0.1" class="form-range slider" bind:value={settings.taper} list="steplist-taper"/>
                            <datalist id="steplist-taper">
                                <option>2</option>
                            </datalist>
                        </div>
                        <div class="settings-slider-label">
                            <div>More Readiness | More Durability</div>
                            <div>{settings.taper}</div>
                            <div>Less Readiness  | Less Durability</div>
                        </div>
                    </div>
                    <div class="settings-field">
                        <div class="dropdown-title align-self-baseline">
                            Maximum Weekly Hours
                            <a href="https://baronbiosys.com/glossary/mex-weekly-hours/" target="_help">
                                <span class="tooltip-icon"><span class="fa-light fa-circle-question"></span></span>
                            </a>
                        </div>
                        <div class="slider-container">
                            <input id="power-avg-control" type="range" min={maxWeeklyHoursBounds.min} max={maxWeeklyHoursBounds.max} step={maxWeeklyHoursBounds.step} class="form-range slider" bind:value={settings.maxWeeklyHours} list="steplist-polarization"/>
                        </div>
                        <div class="settings-slider-label">
                            <div>{maxWeeklyHoursBounds.min}</div>
                            <div>{settings.maxWeeklyHours}</div>
                            <div>{maxWeeklyHoursBounds.max}</div>
                        </div>
                    </div>
                    <div class="settings-field">
                        <div class="dropdown-title align-self-baseline">
                            Polarization Level
                            <a href="https://baronbiosys.com/glossary/polarization-level/" target="_help">
                                <span class="tooltip-icon"><span class="fa-light fa-circle-question"></span></span>
                            </a>
                        </div>
                        <div class="slider-container">
                            <input id="power-avg-control" type="range" min="1" max="5" class="form-range slider" bind:value={settings.polarization} list="steplist-polarization"/>
                            <datalist id="steplist-polarization">
                                <option>4</option>
                            </datalist>
                        </div>
                        <div class="settings-slider-label">
                            <div>1:1</div>
                            <div>{polarizationValues[settings.polarization]}</div>
                            <div>5:1</div>
                        </div>
                    </div>
                    <div class="settings-field">
                        <div class="dropdown-title align-self-baseline">
                            Periodization Level
                            <a href="https://baronbiosys.com/glossary/periodization-level/" target="_help">
                                <span class="tooltip-icon"><span class="fa-light fa-circle-question"></span></span>
                            </a>    
                        </div>
                        <div class="slider-container">
                            <input type="range" min="0.0" max="0.05" step="0.005" class="form-range slider" bind:value={settings.periodization} list="steplist-periodization"/>
                            <datalist id="steplist-periodization">
                                <option>0.025</option>
                            </datalist>
                        </div>
                        <div class="settings-slider-label">
                            <div>None</div>
                            <div>{(settings.periodization*100.0).toFixed(1)} % </div>
                            <div>5%</div>
                        </div>
                    </div> -->
                    <!-- <div class="settings-field">
                        <div class="dropdown-title align-self-baseline">
                            Program Difficulty
                            <a href="https://baronbiosys.com/glossary/program-difficulty" target="_help">
                                <span class="tooltip-icon"><span class="fa-light fa-circle-question"></span></span>
                            </a>
                        </div>
                        <div class="slider-container difficulty-slider-grid">
                            <input disabled={settings.difficultyFromTrainingLoad} type="range" min={50} max={150} step={10} class="form-range slider" bind:value={settings.difficulty}/>
                            <div class="toggle-wrapper gap-1 whitespace-nowrap ml-2">
                                <input class="hidden" type="checkbox" id="difficulty-training-load" bind:checked={settings.difficultyFromTrainingLoad}/><label for="difficulty-training-load"/>
                                <span>Auto-adjust using Training Load</span>
                            </div>
                            <div class="settings-slider-label">
                                <div>50</div>
                                <div>{settings.difficulty?.toFixed(0) ?? ""}</div>
                                <div>150</div>
                            </div>
                        </div>
                    </div> -->
                <!-- </div>
            </div>
        </div> -->
    </div>
</div>


<div id="recalcModal" class="modal fade" role="dialog">
    <div class="modal-dialog">
        <!-- Modal content-->
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="reset-model-title">
                    <i class="fa fa-warning"></i>
                    <b>Reset/Recalculate Entire Progression</b>
                </h4>
                <button type="button" class="modal-close" data-dismiss="modal">&times;
                </button>
            </div>
            <div class="modal-body">
                <p class="m-2">WARNING: The fitness signature of your first activity on {format(new Date(startingTL?.date) , "EEEE, MMMM do yyyy")} will be set to the fitness signature below and recalculated up to your most recent activity!</p>
                <!-- <p>WARNING: Your fitness signature history is about to be erased and your Signature Decay Method will be reset to Optimized.</p> -->
                <p class="m-2">This process may take several minutes to hours to complete. Your progression history will be reset and your current fitness assessment may change.</p>
                <div class="m-2">
                    <FitnessSignature bind:this={recalculateFitnessSignature} refresh={false} title={"Starting Fitness Signature"}  profileParams={$trainingAdvice} smallSize={true}  ></FitnessSignature>
                </div>
                <div class="m-2">
                    <TrainingLoad bind:this={recalculateTrainingLoad} smallSize={true} title={"Starting Training Load"}></TrainingLoad>
                </div>
            </div> 
            <div class="modal-footer">
                <button id="recalc" class="btn btn-default" data-dismiss="modal" on:click={async (e)=>{await recalculateFitnessSignature?.submitHandler(); await recalculateTrainingLoad?.submitHandler(); $trainingAdvice.signature = recalculateFitnessSignature?.getSignature(); RecalculateProgression(e)}}>
                    Proceed
                </button>
                <button class="btn btn-primary" data-dismiss="modal">
                    Do Not Proceed
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="scss">

    #program-page-container {
        
        box-sizing: border-box;
        min-height: 100%;
        display: flex;
        flex-direction: column;
    }

    #program-settings-modal::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    #program-settings-modal {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
    }

    #program-settings-modal {
        display:flex;
        flex-direction:column;
        flex-grow: 1;
    }

    .program-type-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 16px
    }

    .modal-body {
        padding: 0px;
    }

    // global and > used here to apply style to child component (HeartRateDerivedMetrics),
    // since class can't be directly set on component.
    :global(#hr-params > div) {
        flex-grow: 1;
    }

</style>
    